import { unlocksAtTier } from './layer-stack'
import type {
  BoCatalog,
  ManifestDocument,
  ManifestSection,
  TierRule,
} from './types'

/**
 * The comparable shape of a catalogue, from either source.
 *
 * The BO read and a version's manifest describe the same thing differently — one has sortOrder
 * and retired rows, the other has only what was staged. Both project onto this, which is what
 * makes "what changed since live" answerable at all.
 */
export interface ShapeSection {
  id: string
  kind: 'color' | 'option'
  displayName: string
  swatches: string[]
  options: string[]
  /** Effective tier per swatch or slug; null when no tier reaches it. */
  tiers: Record<string, number | null>
  /** Everything else the editor can change, canonicalised so absent and default read the same. */
  settings: string
}

export interface CatalogueShape {
  sections: ShapeSection[]
}

interface SettingsSource {
  paintOrder?: number | null
  backPaintOrder?: number | null
  renderOpacity?: number | null
  renderFit?: string | null
  tints?: Record<string, string> | null
  visibility?: { minTier?: number; anyOf?: readonly string[] } | null
  lockedDisplay?: string | null
}

function settingsOf(source: SettingsSource): string {
  const tints = source.tints ?? {}
  const visibility = source.visibility ?? {}

  return JSON.stringify({
    paintOrder: source.paintOrder ?? null,
    backPaintOrder: source.backPaintOrder ?? null,
    renderOpacity: source.renderOpacity ?? 1,
    renderFit: source.renderFit ?? 'fill',
    tints: Object.keys(tints)
      .sort()
      .map((k) => [k, tints[k]]),
    visibility: {
      minTier: visibility.minTier ?? null,
      anyOf: [...(visibility.anyOf ?? [])].sort(),
    },
    lockedDisplay: source.lockedDisplay ?? null,
  })
}

/**
 * The palette a PLAYER sees: withdrawn swatches taken out.
 *
 * The same rule the option side already followed. Without it, retiring a colour changed nothing in
 * the diff - the operator removed a shade, opened "what changed" and saw an empty list, with no
 * way to tell whether the removal had registered or whether there was anything to publish.
 *
 * Compared without `#` and upper-cased: both lists are operator input, so one colour reaches here
 * written two ways.
 */
export const offeredSwatches = (
  swatches: string[] | null | undefined,
  retired: string[] | null | undefined,
): string[] => {
  const withdrawn = new Set(
    (retired ?? []).map((hex) => hex.replace(/^#/, '').toUpperCase()),
  )

  return (swatches ?? []).filter(
    (hex) => !withdrawn.has(hex.replace(/^#/, '').toUpperCase()),
  )
}

const tiersOf = (
  keys: string[],
  rule: TierRule | null,
): Record<string, number | null> =>
  keys.reduce<Record<string, number | null>>(
    (acc, key, index) => ({ ...acc, [key]: unlocksAtTier(rule, index, key) }),
    {},
  )

/** Only ACTIVE options: a retired one is gone from a player's view, which is what we compare. */
export function shapeFromCatalogue(catalogue: BoCatalog): CatalogueShape {
  return {
    sections: catalogue.sections.map((section) => {
      const options = catalogue.options
        .filter((o) => o.category === section.id && o.status === 'active')
        .sort((a, b) =>
          a.sortOrder === b.sortOrder
            ? a.slug.localeCompare(b.slug)
            : a.sortOrder - b.sortOrder,
        )
        .map((o) => o.slug)
      const swatches = offeredSwatches(
        section.swatches,
        section.retiredSwatches,
      )

      return {
        id: section.id,
        kind: section.kind,
        displayName: section.displayName ?? section.id,
        swatches,
        options,
        tiers: tiersOf(
          section.kind === 'color' ? swatches : options,
          section.tierRule,
        ),
        settings: settingsOf(section),
      }
    }),
  }
}

function shapeSection(
  def: ManifestSection,
  kind: 'color' | 'option',
): ShapeSection {
  const options = (def.options ?? [])
    .filter((o) => o.status !== 'retired')
    .map((o) => o.slug)
  const swatches = offeredSwatches(def.swatches, def.retiredSwatches)

  return {
    id: def.id,
    kind,
    displayName: def.displayName ?? def.id,
    swatches,
    options,
    tiers: tiersOf(kind === 'color' ? swatches : options, def.tierRule ?? null),
    settings: settingsOf(def),
  }
}

/** The manifest holds every set; the diff is about ONE of them. */
export function shapeFromManifest(
  manifest: ManifestDocument,
  setId: string,
): CatalogueShape {
  const set = manifest.sets.find((s) => s.id === setId)
  if (!set) {
    return { sections: [] }
  }

  return {
    sections: [
      ...set.categories.map((c) => shapeSection(c, 'option')),
      ...(set.colorCategories ?? []).map((c) => shapeSection(c, 'color')),
    ],
  }
}

export type ChangeKind =
  | 'sectionAdded'
  | 'sectionRemoved'
  | 'sectionRenamed'
  | 'optionsAdded'
  | 'optionsRemoved'
  | 'optionsReordered'
  | 'coloursAdded'
  | 'coloursRemoved'
  | 'coloursReordered'
  | 'tiersChanged'
  | 'settingsChanged'

export interface Change {
  kind: ChangeKind
  sectionId: string
  sectionName: string
  /** The slugs or hexes involved. Empty for a reorder, which is about position, not membership. */
  names: string[]
}

export interface CatalogueDiff {
  changes: Change[]
  count: number
}

const byId = (shape: CatalogueShape) =>
  new Map(shape.sections.map((s) => [s.id, s]))

const missing = (from: string[], inList: string[]) =>
  from.filter((x) => !inList.includes(x))

/** Same members, different order. Membership changes are reported as add/remove, not as a move. */
const reordered = (before: string[], after: string[]): boolean =>
  before.length === after.length &&
  before.every((x) => after.includes(x)) &&
  before.some((x, i) => after[i] !== x)

/** Only keys BOTH sides have: an added or removed entry is already its own change. */
const tiersDiffer = (previous: ShapeSection, section: ShapeSection): string[] =>
  Object.keys(section.tiers).filter(
    (key) =>
      key in previous.tiers && previous.tiers[key] !== section.tiers[key],
  )

export function diffCatalogues(
  before: CatalogueShape,
  after: CatalogueShape,
): CatalogueDiff {
  const was = byId(before)
  const now = byId(after)
  const changes: Change[] = []

  const push = (
    kind: ChangeKind,
    section: ShapeSection,
    names: string[] = [],
  ) =>
    changes.push({
      kind,
      sectionId: section.id,
      sectionName: section.displayName,
      names,
    })

  after.sections.forEach((section) => {
    const previous = was.get(section.id)
    if (!previous) {
      push('sectionAdded', section)

      return
    }
    if (previous.displayName !== section.displayName) {
      push('sectionRenamed', section, [previous.displayName])
    }

    const isColour = section.kind === 'color'
    const list = isColour ? section.swatches : section.options
    const before2 = isColour ? previous.swatches : previous.options
    const added = missing(list, before2)
    const removed = missing(before2, list)

    if (added.length) {
      push(isColour ? 'coloursAdded' : 'optionsAdded', section, added)
    }
    if (removed.length) {
      push(isColour ? 'coloursRemoved' : 'optionsRemoved', section, removed)
    }
    const moved = reordered(before2, list)
    if (moved) {
      push(isColour ? 'coloursReordered' : 'optionsReordered', section)
    }

    // Not when the list moved: under a count ladder position IS the tier, so a reorder would
    // otherwise always count twice for one action.
    const movedTiers = moved ? [] : tiersDiffer(previous, section)
    if (movedTiers.length) {
      push('tiersChanged', section, movedTiers)
    }
    if (previous.settings !== section.settings) {
      push('settingsChanged', section)
    }
  })

  before.sections.forEach((section) => {
    if (!now.has(section.id)) {
      push('sectionRemoved', section)
    }
  })

  return { changes, count: changes.length }
}

export interface SectionMarks {
  added: Set<string>
  removed: string[]
}

const ADDED: readonly ChangeKind[] = ['coloursAdded', 'optionsAdded']
const REMOVED: readonly ChangeKind[] = ['coloursRemoved', 'optionsRemoved']

/** What one section gained and lost against the baseline, keyed by slug or hex. */
export function sectionMarks(
  changes: Change[] | undefined,
  sectionId: string,
): SectionMarks {
  const inSection = (changes ?? []).filter((c) => c.sectionId === sectionId)

  return {
    added: new Set(inSection.filter((c) => ADDED.includes(c.kind)).flatMap((c) => c.names)),
    removed: inSection.filter((c) => REMOVED.includes(c.kind)).flatMap((c) => c.names),
  }
}
